import { and, asc, desc, eq, ne } from "drizzle-orm";
import {
  order,
  order_line,
  order_line_modifier,
  payment,
  type PowerSyncDrizzleDB,
} from "@comandero/powersync";
import {
  liveOrderFilters,
  liveOrderLineFilters,
  liveOrderLineModifierFilters,
  livePaymentFilters,
} from "../live/filters";

export function getOrdersQuery(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  status?: "open" | "sent" | "paid" | "void",
) {
  const filters = [
    eq(order.organization_id, organizationId),
    eq(order.branch_id, branchId),
    ...liveOrderFilters(),
  ];
  if (status) {
    filters.push(eq(order.status, status));
  }
  return db
    .select()
    .from(order)
    .where(and(...filters))
    .orderBy(desc(order.opened_at));
}

// ID-scoped (keyed) lookup: details by ID; viewing a deleted row is harmless.
export function getOrderByIdQuery(db: PowerSyncDrizzleDB, orderId: string) {
  return db.select().from(order).where(eq(order.id, orderId)).limit(1);
}

export function getOrderLinesQuery(db: PowerSyncDrizzleDB, orderId: string) {
  return db
    .select()
    .from(order_line)
    .where(and(eq(order_line.order_id, orderId), ...liveOrderLineFilters()))
    .orderBy(order_line.sort_order, order_line.created_at);
}

export function getOrderLineModifiersQuery(db: PowerSyncDrizzleDB, lineId: string) {
  return db
    .select()
    .from(order_line_modifier)
    .where(and(eq(order_line_modifier.order_line_id, lineId), ...liveOrderLineModifierFilters()));
}

export function getPaymentsForOrderQuery(db: PowerSyncDrizzleDB, orderId: string) {
  return db
    .select()
    .from(payment)
    .where(and(eq(payment.order_id, orderId), ...livePaymentFilters()));
}

/**
 * Commands for the KDS: orders sent to the kitchen (status `sent`) from a
 * branch, newest first. The KDS lists their lines and advances statuses.
 */
export function getKitchenOrdersQuery(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
) {
  return db
    .select()
    .from(order)
    .where(
      and(
        eq(order.organization_id, organizationId),
        eq(order.branch_id, branchId),
        eq(order.status, "sent"),
        ...liveOrderFilters(),
      ),
    )
    .orderBy(asc(order.opened_at));
}

/** Kitchen lines of an order that have not yet been served. */
export function getKitchenLinesQuery(db: PowerSyncDrizzleDB, orderId: string) {
  return db
    .select()
    .from(order_line)
    .where(
      and(
        eq(order_line.order_id, orderId),
        ne(order_line.kds_status, "served"),
        ...liveOrderLineFilters(),
      ),
    )
    .orderBy(order_line.sort_order, order_line.created_at);
}
